import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { captureException } from "@sentry/react";
import CircularProgress from "@mui/material/CircularProgress";
import { Button } from "../../Button/Button";
import { Typography } from "../../Typography/Typography";
import { List } from "../../List/List";
import { Popconfirm } from "../../Overlay/Overlay";
import { Tooltip } from "../../Tooltip/Tooltip";
import { CheckCircleOutline as CheckCircleOutlined, DeleteOutline as DeleteOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import {
  ChatToolType,
  ConversationSource,
} from "_graphql-types-frontend/graphql";
import {
  useRangerChatStore,
  selectActiveSlot,
  selectConversationId,
  selectSessionId,
} from "../chatStore";
import { MY_CONVERSATIONS, DELETE_CONVERSATION } from "../graphql";
import { useLoadConversation } from "../useLoadConversation";

const { Text } = Typography;

/** Sidebar row shape used by the three-layer merge below. Server rows
 * (numeric id) and pending placeholder rows (sessionId string) share this
 * shape so the render code doesn't have to branch. Optional/nullable
 * fields match the MY_CONVERSATIONS GraphQL response shape so the server
 * data assigns cleanly without per-field defaulting. */
interface SidebarRow {
  id: string | number;
  summary?: string | null;
  createDate: string;
  lastActivityDate?: string | null;
  hasMutationPlans?: boolean | null;
}

/** Persisted conversation-history entry returned by LOAD_CONVERSATION.
 * The chatContext is JSON-stringified at save time and parsed here. */
/** Compact relative-time label for the conversation row — "now", "5m",
 * "3h", "yesterday", "Mar 15". Full timestamp lives in the tooltip.
 * Exported for unit testing. */
export function formatSidebarTimestamp(input: string | Date): string {
  const date = input instanceof Date ? input : new Date(input);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 2) return "yesterday";
  if (diffDay < 7) return `${diffDay}d`;
  // Older: "Mar 15" style, or include year if not this year
  const thisYear = new Date().getFullYear();
  const opts: Intl.DateTimeFormatOptions =
    date.getFullYear() === thisYear
      ? { month: "short", day: "numeric" }
      : { month: "short", day: "numeric", year: "numeric" };
  return date.toLocaleDateString(undefined, opts);
}

/** Extract tools array from the stored chatContext JSON string */
const HISTORY_PAGE_SIZE = 30;

export function ConversationHistoryTab() {
  // Growing the limit (rather than offset paging / fetchMore) keeps the
  // 15s poll and refetch()es consistent: they always re-run with the full
  // current window, so no cache-merge bookkeeping.
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE_SIZE);
  const { data, loading, refetch } = useQuery(MY_CONVERSATIONS, {
    variables: {
      limit: historyLimit,
      offset: 0,
      sources: [ConversationSource.AgentChat, ConversationSource.ScheduledSkillRun],
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 15_000,
  });
  const [deleteConversation] = useMutation(DELETE_CONVERSATION);
  // Loading a conversation lives in useLoadConversation because the chat panel
  // needs the same behaviour when a notification link asks for one, and this
  // tab exists only while the settings popover is open.
  const handleClick = useLoadConversation();
  const setMenuOpen = useRangerChatStore(s => s.setMenuOpen);
  const completedConversations = useRangerChatStore(s => s.completedConversations);
  const slots = useRangerChatStore(s => s.slots);
  const sessionId = useRangerChatStore(selectSessionId);
  const conversationId = useRangerChatStore(selectConversationId);
  const activeSlot = useRangerChatStore(selectActiveSlot);

  // A real conversation row is "generating" if any slot tracks it as such;
  // placeholder rows (sessionId-keyed) are generating while their slot
  // status is "pending" (no convId yet).
  const isItemGenerating = (itemId: number | string) => {
    if (typeof itemId === "string") {
      return slots.get(itemId)?.status === "pending";
    }
    for (const slot of slots.values()) {
      if (slot.conversationId === itemId && slot.status === "generating") return true;
    }
    return false;
  };
  const isItemCompleted = (itemId: number) => completedConversations.has(itemId);

  const allServerConversations: SidebarRow[] =
    data?.myConversations?.items ?? [];
  const serverIds = new Set<number>(
    allServerConversations.map(c => c.id as number)
  );

  // Build the sidebar in three layers, top to bottom:
  //
  // 1. Pending placeholders — slots whose dispatch is in flight but whose
  //    convId hasn't arrived yet. Keyed by sessionId. Without this, hitting
  //    "New Chat" before the first stream event makes the previous chat
  //    vanish from the sidebar until SlotWatcher receives the convId.
  //
  // 2. In-flight slot rows — slots with a convId that the server's
  //    MY_CONVERSATIONS list hasn't returned yet (the server commits the
  //    Conversation row mid-stream, but the next poll/refetch may still be
  //    away).
  //
  // 3. Server conversations from MY_CONVERSATIONS. Deduped against in-flight
  //    slot rows by convId, and against pending placeholders by createDate
  //    proximity (the placeholder's slot.createdAt should match the
  //    server-side row's createDate to within a few seconds).
  const pendingPlaceholders: SidebarRow[] = [];
  const inFlightSlotConversations: SidebarRow[] = [];
  for (const slot of slots.values()) {
    if (slot.conversationId === null) {
      if (slot.status === "pending") {
        pendingPlaceholders.push({
          id: slot.sessionId,
          summary: "Untitled conversation",
          createDate: new Date(slot.createdAt).toISOString(),
          lastActivityDate: null,
          hasMutationPlans: false,
        });
      }
      continue;
    }
    if (serverIds.has(slot.conversationId)) continue;
    if (slot.status !== "generating" && slot.status !== "done") continue;
    inFlightSlotConversations.push({
      id: slot.conversationId,
      summary: "Untitled conversation",
      createDate: new Date(slot.createdAt).toISOString(),
      lastActivityDate: null,
      hasMutationPlans: false,
    });
  }

  // Suppress server rows whose createDate is within 10s of a pending
  // placeholder's slot.createdAt — they're almost certainly the same
  // conversation (we can't link them deterministically because the slot
  // doesn't have a convId yet). Once the slot's SlotWatcher attaches its
  // convId, the slot graduates to inFlightSlotConversations and the server
  // row will be deduped via convId on the next render.
  const PROXIMITY_MS = 10_000;
  const suppressedServerIds = new Set<number>();
  if (pendingPlaceholders.length > 0) {
    const slotCreates = pendingPlaceholders.map(p =>
      new Date(p.createDate).getTime()
    );
    for (const s of allServerConversations) {
      const sCreate = new Date(s.createDate).getTime();
      if (slotCreates.some(slotT => Math.abs(slotT - sCreate) < PROXIMITY_MS)) {
        suppressedServerIds.add(s.id as number);
      }
    }
  }
  const serverConversations = allServerConversations.filter(
    c => !suppressedServerIds.has(c.id as number)
  );

  // Number slot-derived rows when 2+ are visible (concurrent new chats).
  // Anchored to slot.createdAt (oldest = 1) for stable numbering across
  // re-renders. Server rows never get numbered — they'd churn whenever the
  // server list reshuffles, and they typically have a real summary anyway.
  const slotRows: SidebarRow[] = [...pendingPlaceholders, ...inFlightSlotConversations];
  const slotNumber = new Map<string | number, number>();
  if (slotRows.length >= 2) {
    slotRows
      .slice()
      .sort(
        (a, b) =>
          new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
      )
      .forEach((c, i) => slotNumber.set(c.id, i + 1));
  }
  const numberSlotRow = (c: SidebarRow): SidebarRow =>
    slotNumber.has(c.id)
      ? { ...c, summary: `Untitled conversation ${slotNumber.get(c.id)}` }
      : c;

  const conversations: SidebarRow[] = [
    ...pendingPlaceholders.map(numberSlotRow),
    ...inFlightSlotConversations.map(numberSlotRow),
    ...serverConversations,
  ];

  // Refetch conversation list when the active conversation changes (new conversation created).
  // Also schedule a delayed refetch to pick up the async-generated summary.
  useEffect(() => {
    refetch();
    const timer = setTimeout(() => refetch(), 3_000);
    return () => clearTimeout(timer);
  }, [conversationId, refetch]);

  // Only show spinner on initial load (no cached data yet)
  if (loading && !data) {
    return <CircularProgress size={20} />;
  }

  if (conversations.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        No past conversations found.
      </Text>
    );
  }

  return (
    // overflowX:hidden guarantees the history panel never scrolls sideways even
    // if a future row has unbreakable content; rows truncate within the width.
    <div style={{ overflowX: "hidden", width: "100%" }}>
      <List
        size="small"
        dataSource={conversations}
        renderItem={(item: SidebarRow) => {
          // Pending placeholders use sessionId (string) as their id; real
          // conversations have numeric ids. Placeholders are non-interactive
          // (no real convId yet to load or delete). Narrow once at the top
          // so all the number-typed call sites below get proper typing
          // without per-callsite casts.
          const numericId = typeof item.id === "number" ? item.id : null;
          const isPlaceholder = numericId === null;
          return (
          <List.Item
            data-cy="conversation-history-item"
            role="button"
            tabIndex={isPlaceholder ? -1 : 0}
            aria-label={`Load conversation: ${item.summary || "Untitled conversation"}`}
            style={{ cursor: isPlaceholder ? "default" : "pointer", padding: "4px 0", opacity: isPlaceholder ? 0.85 : 1 }}
            onClick={() => { if (numericId !== null) handleClick(numericId); }}
            onKeyDown={e => { if (numericId !== null && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleClick(numericId); } }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 4,
              }}
            >
              {isItemGenerating(item.id) && (
                <Box
                  data-testid={`generating-indicator-${item.id}`}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "#1976d2",
                    flexShrink: 0,
                    animation: "pulse 1.5s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.4 },
                    },
                  }}
                />
              )}
              {numericId !== null && !isItemGenerating(item.id) && isItemCompleted(numericId) && (
                <CheckCircleOutlined
                  style={{ fontSize: 10, color: "#2e7d32", flexShrink: 0 }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Tooltip
                  title={item.summary || "Untitled conversation"}
                  placement="left"
                  mouseEnterDelay={0.4}
                >
                  <Text
                    ellipsis
                    data-cy="conversation-history-summary"
                    // display:block is required for the ellipsis: Typography.Text
                    // renders an inline <span>, and overflow/text-overflow/nowrap
                    // are no-ops on inline elements — so long summaries never
                    // truncated and pushed the panel into a horizontal scroll.
                    // (The timestamp Text below already does this.)
                    style={{ fontSize: 12, fontWeight: 500, display: "block" }}
                  >
                    {item.summary || "Untitled conversation"}
                  </Text>
                </Tooltip>
                <Tooltip
                  title={new Date(item.lastActivityDate ?? item.createDate).toLocaleString()}
                  placement="left"
                  mouseEnterDelay={0.4}
                >
                  <Text
                    type="secondary"
                    style={{ fontSize: 10, display: "block" }}
                  >
                    {formatSidebarTimestamp(item.lastActivityDate ?? item.createDate)}
                  </Text>
                </Tooltip>
              </div>
              {item.hasMutationPlans && (
                <Tooltip title="Has staged mutations" placement="top" mouseEnterDelay={0.3}>
                  <AccountTreeIcon sx={{ fontSize: 14, color: "#7c4dff", flexShrink: 0 }} />
                </Tooltip>
              )}
              {numericId !== null && (
                <Popconfirm
                  title="Delete this conversation?"
                  placement="topLeft"
                  onConfirm={async e => {
                    e?.stopPropagation();
                    await deleteConversation({
                      variables: { conversationId: numericId },
                    });
                    refetch();
                  }}
                  onCancel={e => e?.stopPropagation()}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <DeleteOutlined
                    onClick={e => e.stopPropagation()}
                    style={{ fontSize: 12, color: "#999", flexShrink: 0 }}
                  />
                </Popconfirm>
              )}
            </div>
          </List.Item>
          );
        }}
      />
      {(data?.myConversations?.total ?? 0) > allServerConversations.length && (
        <Button
          type="link"
          size="small"
          data-cy="conversation-history-show-older"
          loading={loading}
          onClick={() => setHistoryLimit(l => l + HISTORY_PAGE_SIZE)}
          style={{ padding: 0, fontSize: 12 }}
        >
          Show older conversations (
          {(data?.myConversations?.total ?? 0) - allServerConversations.length}{" "}
          more)
        </Button>
      )}
    </div>
  );
}
